import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { useHistory, Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { ThemedButton } from '../../components/themed/ThemedComponents';
import pluralize from 'pluralize';
import AddCompanyModal from '../../components/partner/AddCompanyModal';
import { Heading2, Heading4 } from '../../components/design-system/headings';
import { colors } from '../../components/design-system/colors';
import { BigModal } from '../../components/design-system/modals';
import {
    TextTiny,
    TextDefault,
    TextLead,
} from '../../components/design-system/texts';
import {
    Block,
    FlexDiv,
    SpaceBetweenDiv,
} from '../../components/design-system/containers';
import { useStores } from '../../hooks/use-stores';
import SimpleTable from '../../components/SimpleTable';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import { SearchInput } from '../../components/design-system/inputs';
import StatusBadge from '../../components/StatusBadge';
import SubscriptionOverview from './SubscriptionOverview';
import MoreIcon from '@material-ui/icons/MoreVert';
import AddCompanyConfirm from '../../components/partner/AddCompanyConfirm';
import RemoveSubscriptionConfirm from '../../components/partner/RemoveSubscriptionConfirm';
import StopCampaignsConfirm from '../../components/partner/StopCampaignsConfirm';

export default observer(({ partnerId }) => {
    const { store, superStore, commonStore, companyStore, subscriptionStore } =
        useStores();

    const { partnerEstimate, estimationLoading, companyForm } =
        subscriptionStore;
    const {
        loadingPartnerCompanies,
        filteredPartnerCompanies,
        loadingPartnerSubscriptionsOverview,
        partnerSubscriptionsOverview,
        companySearchValue,
        companies,
        activeCompaniesCount,
        inactiveCompaniesCount,
    } = superStore;

    const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [company, setOpenedCompany] = useState(null);
    const [deletingCompany, setDeletingCompany] = useState(false);

    let history = useHistory();
    const now = useMemo(() => new Date(), []);

    useEffect(() => {
        loadData();
    }, [loadData, partnerId]);

    const loadData = useCallback(async () => {
        await superStore.loadPartnerSubscriptionsOverview(partnerId);
        await superStore.loadPartnerCompanies(partnerId);
        await subscriptionStore.loadSubscriptionPlans(partnerId);
    }, [partnerId, subscriptionStore, superStore]);

    const handleSubmitAddNewCompany = useCallback(() => {
        setShowNewCompanyModal(false);
        commonStore.showConfirm(
            subscriptionStore.subscriptionPlans.filter(
                (s) =>
                    s.code ===
                    subscriptionStore.companyForm.values().subscription
            )[0].isFree ? (
                <FlexDiv column>
                    <TextLead mb={16}>
                        You are creating a Free plan for{' '}
                        {subscriptionStore.companyForm.values().companyName}.
                    </TextLead>
                    <TextLead>
                        The Free plan available for up to 10 users includes
                        selected training subjects and assessment questions.
                    </TextLead>
                </FlexDiv>
            ) : (
                <AddCompanyConfirm
                    companyForm={subscriptionStore.companyForm}
                    partnerEstimate={subscriptionStore.partnerEstimate}
                />
            ),
            'Confirm',
            'Confirm new company',
            async () => {
                await companyStore.handleCreateCompany(partnerId, loadData);
            },

            false
        );
    }, [
        commonStore,
        companyStore,
        loadData,
        partnerId,
        subscriptionStore.companyForm,
        subscriptionStore.partnerEstimate,
        subscriptionStore.subscriptionPlans,
    ]);

    const openNewCompanyModal = () => {
        subscriptionStore.initCompanyForm(partnerId, handleSubmitAddNewCompany);
        setShowNewCompanyModal(true);
    };

    const closeNewCompanyModal = () => {
        setShowNewCompanyModal(false);
        subscriptionStore.setPartnerEstimate(null);
    };

    const onDelete = async (
        companyId,
        companyName,
        subscriptionCode,
        subscriptionName,
        isInactive
    ) => {
        setDeletingCompany(true);
        let canBeRemovedResult = await subscriptionStore.canBeRemoved(
            partnerId,
            companyId
        );
        if (canBeRemovedResult && canBeRemovedResult.canBeRemoved) {
            let estimationResult = await subscriptionStore.loadEstimation(
                partnerId,
                subscriptionCode,
                0,
                companyId
            );
            if (estimationResult) {
                setOpenedCompany(null);
                commonStore.showConfirm(
                    <RemoveSubscriptionConfirm
                        company={company}
                        partnerEstimate={subscriptionStore.partnerEstimate}
                        verb={'delete'}
                        action={'deleting'}
                        isFree={
                            subscriptionStore.subscriptionPlans.filter(
                                (s) => s.code === subscriptionCode
                            )[0].isFree
                        }
                        subscriptionName={subscriptionName}
                        isInactive={isInactive}
                    />,
                    'Yes, delete',
                    'Delete company',
                    async () => {
                        let deletionResult = await superStore.deleteCompany(
                            partnerId,
                            companyId
                        );
                        if (deletionResult) {
                            loadData();
                            commonStore.success('Company deleted.');
                        } else {
                            if (superStore.error) {
                                commonStore.error(superStore.error);
                            }
                        }
                    },
                    true
                );
            } else {
                setOpenedCompany(null);
                commonStore.showConfirm(
                    <StopCampaignsConfirm
                        action={'delete ' + companyName}
                        canBeRemovedResult={canBeRemovedResult}
                    />,
                    'Ok',
                    'Unable to delete company',
                    () => {},
                    false,
                    false
                );
            }
        }
        setDeletingCompany(false);
    };

    const visitDashboard = async (companyId) => {
        await store.setCurrentCompany(companyId);
        await history.push(`/`);
    };

    const totalAllotments = companies.reduce((allotments, company) => {
        return (
            allotments +
            (company.allowedUsers ? parseInt(company.allowedUsers) : 0)
        );
    }, 0);
    const employeeTotal = companies
        .filter((c) => c.valid_to !== null)
        .reduce(
            (totalEmployees, company) => totalEmployees + company.userCount,
            0
        );

    const handleMenuOpen = (e, company) => {
        setOpenedCompany(company);
        setAnchorEl(e.currentTarget);
        e.stopPropagation();
    };

    const handleMenuClose = (e) => {
        setOpenedCompany(null);
        setAnchorEl(null);
        e.stopPropagation();
    };

    const moreActionsMenu = useCallback(
        (company) => (
            <IconButton
                color="primary"
                aria-controls="simple-menu"
                aria-haspopup="true"
                onClick={(e) => {
                    handleMenuOpen(e, company);
                    e.preventDefault();
                }}
                id={company.id}
            >
                <MoreIcon />
            </IconButton>
        ),
        []
    );

    const columns = useMemo(
        () => [
            {
                Header: 'Company',
                accessor: 'name',
                Cell: (x) => {
                    return <Heading4>{x.value}</Heading4>;
                },
            },
            {
                Header: 'Subscription',
                accessor: 'subscriptionName',
            },
            {
                Header: 'Seats',
                accessor: 'allowedUsers',
                Cell: (x) => (x.value !== null ? x.value : '-'),
            },
            {
                Header: 'Employees',
                accessor: 'userCount',
            },
            {
                Header: 'Subscription status',
                accessor: 'active',
                Cell: (x) => {
                    const validTo = x.row.original.valid_to;
                    return validTo && new Date(validTo) > now ? (
                        <StatusBadge color={'success'} text={'Active'} />
                    ) : (
                        <StatusBadge color={'danger'} text={'Inactive'} />
                    );
                },
            },
            {
                Header: 'Active until',
                accessor: 'valid_to',
                Cell: (x) => {
                    return (
                        <div
                            style={
                                x.value && new Date(x.value) > now
                                    ? {}
                                    : { color: colors.error }
                            }
                        >
                            {x.value
                                ? moment(x.value).format('DD/MM/YYYY')
                                : '-'}
                        </div>
                    );
                },
            },
            {
                Header: 'Monthly cost',
                accessor: 'price',
                Cell: (x) => {
                    const validTo = x.row.original.valid_to;
                    return validTo && new Date(validTo) > now
                        ? `$${(x.value || 0).toFixed(2)}`
                        : `-`;
                },
            },
            {
                Header: ' ',
                Cell: (x) => moreActionsMenu(x.row.original),
            },
        ],
        [moreActionsMenu, now]
    );

    return (
        <Fragment>
            <SubscriptionOverview
                partnerId={partnerId}
                partnerSubscriptionsOverview={partnerSubscriptionsOverview}
                loadingPartnerSubscriptionsOverview={
                    loadingPartnerSubscriptionsOverview
                }
            />
            <Block>
                <SpaceBetweenDiv>
                    <FlexDiv column>
                        <Heading2>Companies</Heading2>
                        <TextDefault>
                            {companies ? companies.length : 0}{' '}
                            {pluralize('company', companies.length)}
                            {(companies.length !== activeCompaniesCount ||
                                inactiveCompaniesCount > 0) && (
                                <>
                                    {' '}
                                    ({activeCompaniesCount} active,{' '}
                                    {inactiveCompaniesCount} inactive)
                                </>
                            )}{' '}
                            · {employeeTotal} / {totalAllotments} seats
                            allocated
                        </TextDefault>
                    </FlexDiv>
                    <FlexDiv>
                        <SearchInput
                            label=""
                            placeholder="Search companies"
                            searchValue={companySearchValue}
                            onChange={(e) => {
                                superStore.setCompanySearchValue(
                                    e.target.value
                                );
                            }}
                            onClear={() => superStore.setCompanySearchValue('')}
                        />
                        <ThemedButton
                            secondary
                            airy
                            onClick={openNewCompanyModal}
                        >
                            Add Company
                        </ThemedButton>
                    </FlexDiv>
                </SpaceBetweenDiv>
                {loadingPartnerCompanies === null ||
                loadingPartnerCompanies ||
                companyStore.creatingCompany ||
                superStore.removingCompanies.length ? (
                    <ThemedSpinner />
                ) : (
                    <FlexDiv mt={48}>
                        {filteredPartnerCompanies && (
                            <SimpleTable
                                data={filteredPartnerCompanies}
                                columns={columns}
                                useLink
                            />
                        )}
                    </FlexDiv>
                )}
                {company && (
                    <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={true}
                        onClose={handleMenuClose}
                        elevation={1}
                    >
                        <MenuItem onClick={() => visitDashboard(company.id)}>
                            <TextTiny bold>Go to dashboard</TextTiny>
                        </MenuItem>
                        <MenuItem
                            component={Link}
                            to={`/partner/${partnerId}/companies/${company.id}`}
                        >
                            <TextTiny bold>Edit subscription</TextTiny>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                onDelete(
                                    company.id,
                                    company.company_name,
                                    company.subscriptionCode,
                                    company.subscriptionName,
                                    company.price === null
                                );
                            }}
                        >
                            {deletingCompany ? (
                                <Spinner size={30} />
                            ) : (
                                <TextTiny bold error>
                                    Delete
                                </TextTiny>
                            )}
                        </MenuItem>
                    </Menu>
                )}
            </Block>
            <BigModal
                isOpen={showNewCompanyModal}
                onRequestClose={closeNewCompanyModal}
                heading="Add new company"
                modalContent={
                    <AddCompanyModal
                        companyForm={companyForm}
                        partnerId={partnerId}
                        partnerEstimate={subscriptionStore.partnerEstimate}
                        estimationLoading={estimationLoading}
                    />
                }
                modalFooterContent={
                    <SpaceBetweenDiv>
                        <FlexDiv column>
                            <TextTiny lighter bold>
                                Total
                            </TextTiny>
                            {!estimationLoading && !partnerEstimate && (
                                <TextTiny bold lighter>
                                    –
                                </TextTiny>
                            )}
                            {estimationLoading ? (
                                <Spinner />
                            ) : (
                                partnerEstimate && (
                                    <FlexDiv>
                                        <TextLead bold>
                                            ${partnerEstimate.newCompanyTotal}
                                        </TextLead>
                                        <TextLead lighter bold>
                                            /month
                                        </TextLead>
                                    </FlexDiv>
                                )
                            )}
                        </FlexDiv>
                        <FlexDiv>
                            <ThemedButton
                                secondary
                                onClick={closeNewCompanyModal}
                            >
                                Cancel
                            </ThemedButton>
                            <ThemedButton
                                disabled={estimationLoading}
                                primary
                                airy
                                onClick={() => companyForm.submit()}
                            >
                                Review and confirm
                            </ThemedButton>
                        </FlexDiv>
                    </SpaceBetweenDiv>
                }
            />
        </Fragment>
    );
});
